<template>
  <span v-resize="checkMobile">
    <loading
      :active.sync="isLoading"
      :is-full-page="true"
      color="#1976d2"
      :background-color="$vuetify.theme.dark ? '#000' : ''"
    ></loading>

    <v-container v-if="ticket" fluid>
      <!-- Информация о тикете -->
      <v-card class="mb-4">
        <v-card-title class="d-flex align-center" v-if="!isMobile">
          <v-btn
            icon
            @click="$router.go(-1)"
            class="mr-3"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <v-row>
            <v-col cols="12">
              <h2 class="mb-1 text-center">{{ ticket.title }}</h2>
            </v-col>
            <v-col cols="12" class="d-flex align-center flex-wrap justify-center">
              <v-chip
                :color="getStatusColor(ticket.status)"
                dark
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.status_${ticket.status}`) }}
              </v-chip>

              <v-chip
                :color="getPriorityColor(ticket.priority)"
                dark
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.priority_${ticket.priority}`) }}
              </v-chip>

              <v-chip
                v-if="ticket.category"
                outlined
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.category_${ticket.category}`) }}
              </v-chip>
            </v-col>
          </v-row>

          <v-menu
            v-if="canCloseTicket"
            bottom
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="showCloseDialog = true">
                <v-list-item-icon>
                  <v-icon>mdi-close-circle</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ $t('support.close_ticket') }}</v-list-item-title>
                </v-list-item-content>
                </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-card-title class="pb-2" v-else>
          <v-row no-gutters align="center">
            <v-col cols="auto">
              <v-btn
                icon
                @click="$router.go(-1)"
                class="mr-3"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </v-col>

            <v-col class="flex-grow-1">
              <h2 class="mb-0 text-break text-base font-weight-bold text-h6">
                {{ ticket.title }}
              </h2>
            </v-col>

            <v-col cols="auto" v-if="canCloseTicket">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="showCloseDialog = true">
                    <v-list-item-icon>
                      <v-icon>mdi-close-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t('support.close_ticket') }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="12" class="d-flex align-center flex-wrap justify-center">
              <v-chip
                :color="getStatusColor(ticket.status)"
                dark
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.status_${ticket.status}`) }}
              </v-chip>

              <v-chip
                :color="getPriorityColor(ticket.priority)"
                dark
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.priority_${ticket.priority}`) }}
              </v-chip>

              <v-chip
                v-if="ticket.category"
                outlined
                small
                class="mr-2 mb-1"
              >
                {{ $t(`support.category_${ticket.category}`) }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <v-divider></v-divider>
          </div>

          <div class="mb-4">
            <strong>{{ $t('support.description') }}:</strong>
            <div class="mt-2" style="white-space: pre-line">{{ ticket.description }}</div>
          </div>

          <v-row class="text-caption text--secondary">
            <v-col cols="12" sm="6">
              <strong>{{ $t('support.created_at') }}:</strong>
              {{ formatDate(ticket.created_at) }}
            </v-col>
            <v-col cols="12" sm="6" v-if="ticket.closed_at">
              <strong>{{ $t('support.closed_at') }}:</strong>
              {{ formatDate(ticket.closed_at) }}
            </v-col>
          </v-row>

          <!-- Блок с оценкой -->
          <div v-if="ticket.rating || ticket.can_be_rated" class="mt-4">
            <v-divider class="mb-3"></v-divider>

            <!-- Отображение существующей оценки -->
            <div v-if="ticket.rating">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-2">mdi-star</v-icon>
                <strong>{{ $t('support.rating') }}:</strong>
                <v-rating
                  :value="ticket.rating"
                  color="amber"
                  background-color="grey lighten-1"
                  readonly
                  dense
                  size="20"
                  class="ml-2"
                ></v-rating>
                <span class="ml-2 text-body-2">{{ getRatingText(ticket.rating) }}</span>
              </div>

              <div v-if="ticket.rating_comment" class="text-body-2 ml-8">
                <strong>{{ $t('support.rating_comment') }}:</strong>
                <div class="mt-1" style="white-space: pre-line">{{ ticket.rating_comment }}</div>
              </div>

              <div v-if="ticket.rated_at" class="text-caption text--secondary ml-8 mt-1">
                {{ $t('support.rated_at') }}: {{ formatDate(ticket.rated_at) }}
              </div>
            </div>

            <!-- Форма для оценки -->
            <div v-else-if="ticket.can_be_rated && !showRatingForm">
              <v-btn
                color="primary"
                small
                outlined
                @click="showRatingForm = true"
              >
                <v-icon left small>mdi-star-outline</v-icon>
                {{ $t('support.rate_support') }}
              </v-btn>
            </div>

            <!-- Форма оценки -->
            <div v-if="showRatingForm && ticket.can_be_rated">
              <h4 class="mb-3">{{ $t('support.rate_support') }}</h4>

              <div class="mb-3">
                <div class="mb-2">{{ $t('support.select_rating') }}:</div>
                <v-rating
                  v-model="ratingForm.rating"
                  color="amber"
                  background-color="grey lighten-1"
                  large
                  hover
                ></v-rating>
                <div v-if="ratingForm.rating" class="text-body-2 mt-1">
                  {{ getRatingText(ratingForm.rating) }}
                </div>
              </div>

              <v-textarea
                v-model="ratingForm.comment"
                :label="$t('support.rating_comment_optional')"
                rows="3"
                counter="1000"
                outlined
                dense
              ></v-textarea>

              <div class="d-flex justify-end">
                <v-btn
                  text
                  @click="cancelRating"
                  class="mr-2"
                >
                  {{ $t('custom.close') }}
                </v-btn>
                <v-btn
                  color="success"
                  :disabled="!ratingForm.rating"
                  :loading="isSubmittingRating"
                  @click="submitRating"
                >
                  {{ $t('support.submit_rating') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Сообщения -->
      <v-card>
        <v-card-title>
          {{ $t('support.messages') }}
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            @click="loadTicketData"
          >
            <v-icon small left>mdi-refresh</v-icon>
            {{ $t('custom.refresh') }}
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="messages.length === 0" class="text-center text--secondary py-4">
            {{ $t('support.no_messages') }}
          </div>

          <div v-else class="mb-4">
            <v-timeline dense>
              <v-timeline-item
                v-for="message in publicMessages"
                :key="message.id"
                color="blue"
              >
                <template v-slot:icon>
                  <v-icon small>mdi-message</v-icon>
                </template>

                <v-card>
                  <v-card-title class="pb-2">
                    <div class="d-flex align-center w-100">
                      <div v-if="isSupport(message.user.id)">
                        <strong>{{ $t('support.support_team') }}</strong>
                      </div>
                      <div v-else>
                        <strong>{{ message.user ? message.user.name || message.user.email : "?" }}</strong>
                      </div>
                      <v-spacer></v-spacer>
                      <small class="pl-2 text--secondary">
                        {{ formatDate(message.created_at) }}
                      </small>
                    </div>
                  </v-card-title>

                  <v-card-text>
                    <div style="white-space: pre-line">{{ message.content }}</div>

                    <!-- Отображение вложений -->
                    <div v-if="message.attachments && message.attachments.length > 0" class="mt-3">
                      <v-divider class="mb-2"></v-divider>
                      <div class="text-caption text--secondary mb-2">{{ $t('support.attachments') }}:</div>
                      <div class="d-flex flex-wrap">
                        <v-chip
                          v-for="(attachment, index) in message.attachments"
                          :key="index"
                          small
                          class="mr-2 mb-1"
                          color="grey lighten-3"
                          @click="downloadAttachment(attachment)"
                          style="cursor: pointer;"
                        >
                          <v-icon left small>{{ getFileIcon(attachment.type) }}</v-icon>
                          {{ attachment.name }}
                          <span class="ml-1 text-caption">({{ formatFileSize(attachment.size) }})</span>
                        </v-chip>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-timeline-item>
            </v-timeline>
          </div>

          <!-- Форма добавления сообщения -->
          <v-divider class="my-4"></v-divider>

          <div v-if="ticket.status !== 'closed'">
            <h4 class="mb-3">{{ $t('support.add_message') }}</h4>

            <v-textarea
              v-model="newMessage.content"
              :label="$t('support.message_content')"
              rows="3"
              counter="2000"
            ></v-textarea>

            <!-- Загрузка файлов -->
            <v-file-input
              v-model="uploadedFiles"
              :label="$t('support.attach_files')"
              multiple
              show-size
              chips
              prepend-icon="mdi-paperclip"
              accept="image/*,.pdf,.doc,.docx,.txt"
              :disabled="isSendingMessage"
              class="mb-3"
            ></v-file-input>

            <!-- Отображение прогресса загрузки -->
            <div v-if="Object.keys(fileUploadProgress).length > 0" class="mb-3">
              <div class="text-subtitle-2 mb-2">{{ $t('support.uploading_files') }}:</div>
              <div v-for="(progress, fileName) in fileUploadProgress" :key="fileName" class="mb-2">
                <div class="d-flex align-center">
                  <v-icon
                    :color="progress.status === 'completed' ? 'success' : progress.status === 'error' ? 'error' : 'primary'"
                    small
                    class="mr-2"
                  >
                    {{ progress.status === 'completed' ? 'mdi-check-circle' : progress.status === 'error' ? 'mdi-alert-circle' : 'mdi-file-upload' }}
                  </v-icon>
                  <span class="text-caption">{{ fileName }}</span>
                  <v-spacer></v-spacer>
                  <span class="text-caption">{{ progress.progress }}%</span>
                </div>
                <v-progress-linear
                  :value="progress.progress"
                  :color="progress.status === 'completed' ? 'success' : progress.status === 'error' ? 'error' : 'primary'"
                  height="2"
                  class="mt-1"
                ></v-progress-linear>
              </div>
            </div>

            <!-- Отображение загруженных файлов -->
            <div v-if="newMessage.attachments.length > 0" class="mb-3">
              <div class="text-subtitle-2 mb-2">{{ $t('support.attached_files') }}:</div>
              <v-chip
                v-for="(attachment, index) in newMessage.attachments"
                :key="index"
                close
                @click:close="removeAttachment(index)"
                class="mr-2 mb-1"
                color="primary"
                small
              >
                <v-icon left small>mdi-file</v-icon>
                {{ attachment.name }}
              </v-chip>
            </div>

            <div class="d-flex justify-end mt-3">
              <v-btn
                color="success"
                :disabled="!isMessageValid || isSendingMessage || isUploadingFiles"
                :loading="isSendingMessage || isUploadingFiles"
                @click="sendMessage"
              >
                <span v-if="isUploadingFiles">{{ $t('support.uploading_files') }}</span>
                <span v-else>{{ $t('support.send_message') }}</span>
              </v-btn>
            </div>
          </div>

          <div v-else class="text-center text--secondary py-4">
            <v-icon large class="mb-2">mdi-lock</v-icon>
            <div>{{ $t('support.ticket_closed_no_messages') }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <div v-else-if="!isLoading" class="text-center mt-5">
      <v-icon size="64" color="grey">mdi-help-circle-outline</v-icon>
      <h3 class="grey--text mt-3">{{ $t('support.ticket_not_found') }}</h3>
    </div>

    <!-- Диалог подтверждения закрытия тикета -->
    <v-dialog v-model="showCloseDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon color="warning" class="mr-3">mdi-alert</v-icon>
          {{ $t('support.confirm_close_ticket') }}
        </v-card-title>

        <v-card-text>
          <p>{{ $t('support.close_ticket_warning') }}</p>
        </v-card-text>

        <v-card-actions class="justify-center">
          <v-btn text @click="showCloseDialog = false">
            {{ $t('custom.no') }}
          </v-btn>
          <v-btn
            color="warning"
            :loading="isClosingTicket"
            @click="confirmCloseTicket"
          >
            {{ $t('support.yes_close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import moment from 'moment'
import { setMomentLocale } from '@/utils/localeInfra'

export default {
  name: 'SupportTicketDetail',
  data() {
    return {
      isLoading: false,
      isSendingMessage: false,
      isClosingTicket: false,
      isMobile: false,
      showCloseDialog: false,
      showRatingForm: false,
      isSubmittingRating: false,
      ticket: null,
      messages: [],
      newMessage: {
        content: '',
        attachments: [],
      },
      uploadedFiles: [],
      fileUploadProgress: {},
      ratingForm: {
        rating: null,
        comment: '',
      },
    }
  },
  computed: {
    publicMessages() {
      // Показываем только публичные сообщения клиентам
      return this.messages.filter(message => !message.internal)
    },
    isMessageValid() {
      return this.newMessage.content.trim().length > 0
    },
    isUploadingFiles() {
      return Object.values(this.fileUploadProgress).some(progress => progress.status === 'uploading')
    },
    // Флаг can_be_closed отдаёт api (автор, исполнитель или саппорт). Сравнение
    // с false, а не по истинности: api без этого поля не должен прятать пункт.
    canCloseTicket() {
      return this.ticket.status !== 'closed' && this.ticket.can_be_closed !== false
    },
  },

  created() {
    // Настройка локали moment
    setMomentLocale(this.$i18n.locale);

    if (this.$store.getters.isLoggedIn) {
      this.loadTicketData()
    } else {
      this.$store.commit('unsetCurrentUser')
    }
  },
  methods: {
    isSupport(id) {
      return [ 1, 2, 3, 4, 81, 976, 13794 ].includes(id)
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },

    loadTicketData() {
      this.isLoading = true

      this.axios
        .get(`/api/v1/support_tickets/${this.$route.params.id}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          if (response.data) {
            // Проверяем новый комбинированный формат
            if (response.data.ticket) {
              // Новый комбинированный формат
              this.ticket = response.data.ticket.attributes

              // Загружаем сообщения
              this.messages = []
              if (response.data.messages) {
                response.data.messages.forEach((message) => {
                  this.messages.push(message.attributes)
                })
              }
            } else if (response.data.data) {
              // Старый формат (только тикет)
              this.ticket = response.data.data.attributes
              this.loadMessages()
            }
          }
        })
        .catch((error) => {
          // 404 = обращения нет (удалено / чужой доступ / битый id): уводим на
          // список вместо пустой страницы. Прочее — через setError.
          if (error.response?.status === 404) {
            this.$notify(this.$t('support.ticket_not_found'), "", "error")
            this.$router.push({ path: `/support/tickets` })
            return
          }
          this.setError(error, this.$t('support.error_loading_ticket'))
          console.error(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    loadMessages() {
      this.axios
        .get(`/api/v1/support_tickets/${this.$route.params.id}/support_ticket_messages`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.messages = []
          if (response.data && response.data.data) {
            response.data.data.forEach((message) => {
              this.messages.push(message.attributes)
            })
          }
        })
        .catch((error) => {
          this.setError(error, this.$t('support.error_loading_messages'))
          console.error(error)
        })
    },

    async sendMessage() {
      if (!this.isMessageValid) return

      this.isSendingMessage = true

      try {
        // Обрабатываем файлы, если есть - загружаем их в S3
        if (this.uploadedFiles.length > 0) {
          await this.uploadFilesToS3()
        }

        const messageData = {
          support_ticket_message: {
            content: this.newMessage.content.trim(),
            attachments: this.newMessage.attachments,
          }
        }

        console.log(messageData)

        const response = await this.axios.post(
          `/api/v1/support_tickets/${this.$route.params.id}/support_ticket_messages`,
          messageData,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )

        this.$notify(this.$t('support.message_sent_successfully'), "", "success")

        this.newMessage.content = ''
        this.newMessage.attachments = []
        this.uploadedFiles = []
        this.fileUploadProgress = {}
        this.loadTicketData()

      } catch (error) {
        this.setError(error, this.$t('support.error_sending_message'))
        console.error(error)
      } finally {
        this.isSendingMessage = false
      }
    },

    async uploadFilesToS3() {
      this.newMessage.attachments = [] // Очищаем старые attachments
      this.fileUploadProgress = {}

      for (let i = 0; i < this.uploadedFiles.length; i++) {
        const file = this.uploadedFiles[i]

        try {
          // Показываем прогресс
          this.$set(this.fileUploadProgress, file.name, { progress: 0, status: 'uploading' })

          const base64 = await this.fileToBase64(file)
          const fileData = base64.split(',')[1] // убираем data:mime;base64, prefix

          // Проверяем размер файла (клиентская валидация)
          const maxSize = 10 * 1024 * 1024 // 10MB
          if (fileData.length * 0.75 > maxSize) { // base64 увеличивает размер на ~33%
            throw new Error(`Файл "${file.name}" слишком большой. Максимальный размер: 10MB`)
          }

          this.$set(this.fileUploadProgress, file.name, { progress: 50, status: 'uploading' })

          // Загружаем файл в S3
          const uploadResponse = await this.axios.post('/api/v1/support_ticket_file_uploads', {
            file: fileData,
            filename: file.name,
            content_type: file.type
          }, {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          })

          if (uploadResponse.data.success) {
            this.newMessage.attachments.push({
              name: uploadResponse.data.original_filename,
              size: uploadResponse.data.size,
              type: uploadResponse.data.content_type,
              file_key: uploadResponse.data.file_key, // Ключ файла в S3
            })

            this.$set(this.fileUploadProgress, file.name, { progress: 100, status: 'completed' })
          } else {
            throw new Error(uploadResponse.data.error || 'Ошибка загрузки файла')
          }

        } catch (error) {
          console.error('Ошибка при загрузке файла:', error)
          this.$set(this.fileUploadProgress, file.name, { progress: 0, status: 'error' })
          this.$notify('Ошибка при загрузке файла', error.message || `Не удалось загрузить файл ${file.name}`, 'error')
        }
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },

    removeAttachment(index) {
      this.newMessage.attachments.splice(index, 1)
      // Также удаляем соответствующий файл из uploadedFiles
      if (this.uploadedFiles[index]) {
        this.uploadedFiles.splice(index, 1)
      }
    },

    getFileIcon(mimeType) {
      if (!mimeType) return 'mdi-file'

      if (mimeType.startsWith('image/')) return 'mdi-file-image'
      if (mimeType.includes('pdf')) return 'mdi-file-pdf-box'
      if (mimeType.includes('word') || mimeType.includes('document')) return 'mdi-file-word-box'
      if (mimeType.includes('text')) return 'mdi-file-document'

      return 'mdi-file'
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Б'

      const k = 1024
      const sizes = ['Б', 'КБ', 'МБ', 'ГБ']
      const i = Math.floor(Math.log(bytes) / Math.log(k))

      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    async downloadAttachment(attachment) {
      // Поддержка старого формата (base64 data)
      if (attachment.data) {
        try {
          // Конвертируем base64 обратно в blob
          const byteCharacters = atob(attachment.data)
          const byteNumbers = new Array(byteCharacters.length)

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }

          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: attachment.type })

          // Создаем ссылку для скачивания
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = attachment.name
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } catch (error) {
          console.error('Ошибка при скачивании файла:', error)
          this.$notify('Ошибка', 'Не удалось скачать файл', 'error')
        }
        return
      }

      // Новый формат (S3 file_key)
      if (!attachment.file_key) {
        this.$notify('Ошибка', 'Файл недоступен для скачивания', 'error')
        return
      }

      try {
        // Получаем presigned URL для скачивания
        const response = await this.axios.get('/api/v1/support_ticket_file_uploads/presigned_url', {
          params: { file_key: attachment.file_key },
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })

        if (response.data.presigned_url) {
          // Открываем файл в новой вкладке или скачиваем
          const link = document.createElement('a')
          link.href = response.data.presigned_url
          link.download = attachment.name
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else {
          throw new Error('Не удалось получить ссылку для скачивания')
        }
      } catch (error) {
        console.error('Ошибка при получении ссылки для скачивания:', error)
        this.$notify('Ошибка', 'Не удалось скачать файл', 'error')
      }
    },

    confirmCloseTicket() {
      this.isClosingTicket = true

      this.axios
        .patch(`/api/v1/support_tickets/${this.$route.params.id}/close`, {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.$notify(this.$t('support.ticket_closed_successfully'), "", "success");

          this.showCloseDialog = false
          this.loadTicketData()
        })
        .catch((error) => {
          // 403 — осознанный отказ сервера (нет права закрыть), а не сбой:
          // объясняем причину и прячем пункт, в Rollbar не шлём.
          if (error.response && error.response.status === 403) {
            this.$notify(this.$t('support.error_closing_ticket'), this.$t('support.close_ticket_forbidden'), 'warning', 6000)
            this.showCloseDialog = false
            this.$set(this.ticket, 'can_be_closed', false)
            return
          }
          this.setError(error, this.$t('support.error_closing_ticket'))
          console.error(error)
        })
        .finally(() => {
          this.isClosingTicket = false
        })
    },

    getStatusColor(status) {
      const colors = {
        open: 'blue',
        in_progress: 'orange',
        resolved: 'green',
        closed: 'grey'
      }
      return colors[status] || 'grey'
    },

    getPriorityColor(priority) {
      const colors = {
        low: 'green',
        medium: 'orange',
        high: 'red',
        urgent: 'purple'
      }
      return colors[priority] || 'grey'
    },

    formatDate(dateString) {
      return moment(dateString).format('DD.MM.YYYY HH:mm')
    },


    getRatingText(rating) {
      const texts = {
        1: this.$t('support.rating_very_bad'),
        2: this.$t('support.rating_bad'),
        3: this.$t('support.rating_satisfactory'),
        4: this.$t('support.rating_good'),
        5: this.$t('support.rating_excellent')
      }
      return texts[rating] || this.$t('support.rating_not_rated')
    },

    cancelRating() {
      this.showRatingForm = false
      this.ratingForm.rating = null
      this.ratingForm.comment = ''
    },

    async submitRating() {
      if (!this.ratingForm.rating) return

      this.isSubmittingRating = true

      try {
        const ratingData = {
          support_ticket: {
            rating: this.ratingForm.rating,
            rating_comment: this.ratingForm.comment.trim() || null
          }
        }

        const response = await this.axios.patch(
          `/api/v1/support_tickets/${this.$route.params.id}/rate`,
          ratingData,
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )

        this.$notify(this.$t('support.rating_submitted_successfully'), "", "success")

        this.showRatingForm = false
        this.ratingForm.rating = null
        this.ratingForm.comment = ''
        this.loadTicketData()

      } catch (error) {
        this.setError(error, this.$t('support.error_submitting_rating'))
        console.error(error)
      } finally {
        this.isSubmittingRating = false
      }
    },

  },
}
</script>

<style scoped>
.v-timeline-item {
  padding-bottom: 24px;
}

.timeline-content {
  white-space: pre-wrap;
}
</style>
